import { forwardRef, type ReactElement, type ReactNode, type Ref } from 'react'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'
import { cn } from '@/lib/utils'
import { Tooltip } from './Tooltip'
import './tabs.css'

export type TabItem<Value extends string> = {
  value: Value
  /** Visible text, or the accessible name and tooltip when the tabs are icon-only. */
  label: string
  /** A Lucide icon. Required when the tabs are icon-only. */
  icon?: ReactElement
  /**
   * A count shown after the label, for example open tickets, and included in the accessible name. On icon-only tabs it
   * is a corner badge instead (so the tab keeps its width), capped at 99+ and hidden at zero.
   */
  count?: number
  /** What the count means, read after the label in the accessible name (and an icon-only tab's tooltip): “3 new” reads “Chat, 3 new”. Defaults to “Chat (3)”. */
  countLabel?: string
  disabled?: boolean
  /** The panel shown while this tab is selected. */
  content: ReactNode
  /** Keep the panel in the DOM while hidden, for example to preserve unsaved form input. */
  keepMounted?: boolean
}

export type TabsTone = 'default' | 'inverse'

export type TabsProps<Value extends string> = {
  /** Names the tab list for assistive technology, for example “Event settings”. */
  label: string
  items: TabItem<Value>[]
  /** The selected tab, when controlled. */
  value?: Value
  /** The first selected tab, when uncontrolled. Defaults to the first enabled tab. */
  defaultValue?: Value
  onValueChange?: (value: Value) => void
  /** Show only icons, with each label as the accessible name and tooltip. */
  iconOnly?: boolean
  /** Span the container and share its width equally between the tabs, for example two or three tabs on a phone. */
  fill?: boolean
  /**
   * `inverse` is an ink track whose selected tab is yellow, as a checked control: for a switcher floating over media
   * and the page, such as the watch screen's view switcher on a phone.
   */
  tone?: TabsTone
  /** Content beside the tab list, such as filters for the current tab. */
  actions?: ReactNode
  /** Select a tab as soon as arrow keys move focus to it. Turn off when panels are slow to render. */
  activateOnFocus?: boolean
  className?: string
}

function TabsInner<Value extends string>(
  { label, items, value, defaultValue, onValueChange, iconOnly = false, fill = false, tone = 'default', actions, activateOnFocus = true, className }: TabsProps<Value>,
  ref: Ref<HTMLDivElement>,
) {
  const initial = value === undefined ? (defaultValue ?? items.find((item) => !item.disabled)?.value) : undefined
  return (
    <BaseTabs.Root
      ref={ref}
      value={value}
      defaultValue={initial}
      onValueChange={(next) => onValueChange?.(next as Value)}
      className={cn('play-tabs', fill && 'play-tabs--fill', tone === 'inverse' && 'play-tabs--inverse', className)}
    >
      <div className="play-tabs__header">
        <BaseTabs.List aria-label={label} activateOnFocus={activateOnFocus} className="play-tabs__list">
          {items.map((item) => {
            const count = shownCount(item, iconOnly)
            const name = tabName(item, count)
            const tab = (
              <BaseTabs.Tab
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                aria-label={iconOnly || count !== undefined ? name : undefined}
                className={cn('play-tabs__tab', iconOnly && 'play-tabs__tab--icon')}
              >
                {item.icon}
                {!iconOnly && <span>{item.label}</span>}
                {count !== undefined &&
                  (iconOnly ? (
                    <span className="play-tabs__badge" aria-hidden="true">{count > BADGE_MAX ? `${BADGE_MAX}+` : count}</span>
                  ) : (
                    <span className="play-tabs__count" aria-hidden="true">{count}</span>
                  ))}
              </BaseTabs.Tab>
            )
            return iconOnly ? <Tooltip key={item.value} content={name}>{tab}</Tooltip> : tab
          })}
        </BaseTabs.List>
        {actions && <div className="play-tabs__actions">{actions}</div>}
      </div>
      {items.map((item) => (
        <BaseTabs.Panel key={item.value} value={item.value} keepMounted={item.keepMounted} className="play-tabs__panel">
          {item.content}
        </BaseTabs.Panel>
      ))}
    </BaseTabs.Root>
  )
}

/** Icon-only corner badges stop at 99+ so they stay a badge; the tab's name carries the whole count. */
const BADGE_MAX = 99

/** The count's part of the tab, or nothing: an icon-only tab drops a count of zero, as its badge does. */
function shownCount(item: TabItem<string>, iconOnly: boolean) {
  if (item.count === undefined || (iconOnly && item.count <= 0)) return undefined
  return item.count
}

/** The tab's accessible name (and an icon-only tab's tooltip): the label, then what the count means. */
function tabName(item: TabItem<string>, count: number | undefined) {
  if (count === undefined) return item.label
  return item.countLabel ? `${item.label}, ${item.countLabel}` : `${item.label} (${count})`
}

/** Switches between panels of related content in place, such as an event’s General and Course settings. */
export const Tabs = forwardRef(TabsInner) as <Value extends string>(
  props: TabsProps<Value> & { ref?: Ref<HTMLDivElement> },
) => ReactElement
